import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Network, TrendingUp, Users, Eye } from "lucide-react";

interface NetworkReach {
  directConnections: number;
  estimatedSecondDegree: number;
  potentialImpressions: number;
}

interface NetworkReachVisualizerProps {
  networkReach: NetworkReach;
  companyName: string;
  competitorAverage?: number;
}

export const NetworkReachVisualizer = ({ 
  networkReach, 
  companyName,
  competitorAverage 
}: NetworkReachVisualizerProps) => {
  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getComparisonBadge = () => {
    if (!competitorAverage) return null;
    const diff = networkReach.potentialImpressions - competitorAverage;
    const percentage = Math.round((diff / competitorAverage) * 100);
    const isPositive = diff > 0;
    
    return (
      <Badge variant={isPositive ? "default" : "secondary"} className="ml-2">
        {isPositive ? '+' : ''}{percentage}% vs konkurencja
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Network className="h-5 w-5" />
          Network Effect Analysis
        </CardTitle>
        <CardDescription>
          Combined reach potential of {companyName} employees on LinkedIn
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Visual representation */}
          <div className="relative p-8 bg-gradient-to-br from-primary/5 via-background to-primary/10 rounded-lg border border-primary/20">
            <div className="flex items-center justify-center">
              <div className="relative">
                {/* Company center */}
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-background font-bold shadow-lg z-10 relative">
                  {companyName.substring(0, 2).toUpperCase()}
                </div>
                
                {/* Connections circles */}
                <div className="absolute inset-0 -m-16 rounded-full border-2 border-primary/30 border-dashed animate-pulse" />
                <div className="absolute inset-0 -m-24 rounded-full border-2 border-primary/20 border-dashed animate-pulse delay-75" />
                <div className="absolute inset-0 -m-32 rounded-full border-2 border-primary/10 border-dashed animate-pulse delay-150" />
              </div>
            </div>
            
            <div className="absolute top-4 right-4">
              <Badge variant="secondary" className="text-xs">
                Network Multiplier Effect
              </Badge>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  Direct Connections
                </span>
              </div>
              <p className="text-2xl font-bold">
                {formatNumber(networkReach.directConnections)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Total employee connections
              </p>
            </div>

            <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <Network className="h-4 w-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  2nd Degree Reach
                </span>
              </div>
              <p className="text-2xl font-bold">
                {formatNumber(networkReach.estimatedSecondDegree)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Estimated extended network
              </p>
            </div>

            <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="h-4 w-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  Potential Impressions
                </span>
              </div>
              <div className="flex items-center">
                <p className="text-2xl font-bold">
                  {formatNumber(networkReach.potentialImpressions)}
                </p>
                {getComparisonBadge()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Monthly reach potential
              </p>
            </div>
          </div>

          {/* Insights */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Network Leverage</h4>
                <p className="text-sm text-muted-foreground">
                  Your employees' combined network provides access to{' '}
                  <strong className="text-foreground">
                    {formatNumber(networkReach.estimatedSecondDegree)}
                  </strong>{' '}
                  professionals. With strategic employee advocacy, each post can 
                  potentially reach{' '}
                  <strong className="text-foreground">
                    {Math.round(networkReach.potentialImpressions / 100)}+
                  </strong>{' '}
                  relevant decision-makers.
                </p>
              </div>
            </div>
          </div>

          {competitorAverage && (
            <div className="pt-4 border-t border-border/50">
              <p className="text-xs text-muted-foreground text-center">
                Competitor Average: {formatNumber(competitorAverage)} monthly impressions
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
